/**
 * Technician List page per COMP-007 Story 4.
 * Admin-only: displays technicians from Entra ID with tier, clearance, location.
 * Add, edit attributes, and revoke access capabilities.
 */

import { useState, useMemo, useCallback } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Users, Search, UserPlus, Settings, ShieldX } from "lucide-react";
import { StatusBadge } from "@/components/sc/status-badge";
import { EmptyState } from "@/components/sc/empty-state";
import { DataTable, type Column, type SortState } from "@/components/sc/data-table";
import { Button } from "@/components/ui/button";
import { useTechnicianList } from "@/hooks/use-technicians";
import { AddTechnicianDialog } from "@/components/technicians/add-technician-dialog";
import { EditAttributesDialog } from "@/components/technicians/edit-attributes-dialog";
import { RevokeAccessDialog } from "@/components/technicians/revoke-access-dialog";
import type { TechnicianSummary } from "@/types";
import { GlassCard } from "@/components/sc/glass-card";

const TIER_DESCRIPTIONS: Record<string, string> = {
  "Tier-0": "Break-Glass — Global Administrator only",
  "Tier-1": "Senior Tech — 25 roles",
  "Tier-2": "Standard Tech — 14 roles",
  "Tier-3": "Junior / Limited — 10 roles",
  "Tier-4": "Read-Only / Monitoring — 4 roles",
};

export function TechniciansPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sort, setSort] = useState<SortState>({
    column: "displayName",
    direction: "asc",
  });

  // Dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<TechnicianSummary | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<TechnicianSummary | null>(null);

  // Debounce search (DV-001: 300ms)
  const debounceRef = useMemo(() => {
    let timeout: ReturnType<typeof setTimeout>;
    return (value: string) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        setDebouncedSearch(value);
        setPage(1);
      }, 300);
    };
  }, []);

  const techniciansQuery = useTechnicianList({ page, pageSize });
  const technicians = techniciansQuery.data?.items ?? [];
  const total = techniciansQuery.data?.total ?? 0;

  // Client-side search filter (for small datasets <= 100)
  const searched = debouncedSearch
    ? technicians.filter(
        (t) =>
          t.displayName.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
          t.userPrincipalName.toLowerCase().includes(debouncedSearch.toLowerCase()),
      )
    : technicians;

  // Client-side sorting
  const filtered = useMemo(() => {
    if (!sort.column || !sort.direction) return searched;
    return [...searched].sort((a, b) => {
      const key = sort.column as keyof TechnicianSummary;
      const aVal = String(a[key] ?? "");
      const bVal = String(b[key] ?? "");
      const cmp = aVal.localeCompare(bVal, undefined, { sensitivity: "base" });
      return sort.direction === "asc" ? cmp : -cmp;
    });
  }, [searched, sort.column, sort.direction]);

  // Stop click propagation on action buttons (prevent row click navigation)
  const handleActionClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const columns: Column<TechnicianSummary>[] = useMemo(
    () => [
      {
        key: "displayName",
        header: "Name",
        sortable: true,
        className: "max-w-[200px]",
        render: (row) => (
          <span className="font-medium truncate block" title={row.displayName}>
            {row.displayName}
          </span>
        ),
      },
      {
        key: "userPrincipalName",
        header: "Email",
        className: "max-w-[250px]",
        render: (row) => (
          <span
            className="text-sc-text-muted truncate block"
            title={row.userPrincipalName}
          >
            {row.userPrincipalName}
          </span>
        ),
      },
      {
        key: "tier",
        header: "Tier",
        sortable: true,
        render: (row) =>
          row.tier ? (
            <span title={TIER_DESCRIPTIONS[row.tier] ?? row.tier}>
              <StatusBadge status={row.tier} category="techTier" />
            </span>
          ) : (
            <span className="text-body-sm text-sc-text-muted">Not set</span>
          ),
      },
      {
        key: "teamsSpecialist",
        header: "Teams",
        render: (row) =>
          row.teamsSpecialist ? (
            <span className="inline-flex items-center rounded-full bg-teal-100 px-2 py-0.5 text-xs font-medium text-teal-700 dark:bg-teal-900/30 dark:text-teal-300">
              Teams
            </span>
          ) : null,
      },
      {
        key: "securityClearance",
        header: "Clearance",
        render: (row) => (
          <span className="text-body-sm text-sc-text">
            {row.securityClearance ?? "Not set"}
          </span>
        ),
      },
      {
        key: "workLocation",
        header: "Location",
        render: (row) => (
          <span className="text-body-sm text-sc-text">
            {row.workLocation ?? "Not set"}
          </span>
        ),
      },
      {
        key: "accountEnabled",
        header: "Status",
        render: (row) => (
          <span
            className={`inline-flex items-center gap-1 text-body-sm ${
              row.accountEnabled ? "text-sc-success" : "text-sc-text-muted"
            }`}
          >
            <span
              className={`h-2 w-2 rounded-full ${
                row.accountEnabled ? "bg-sc-success" : "bg-gray-400"
              }`}
            />
            {row.accountEnabled ? "Active" : "Disabled"}
          </span>
        ),
      },
      {
        key: "actions" as keyof TechnicianSummary,
        header: "",
        className: "w-[100px]",
        render: (row) => (
          <div
            className="flex items-center gap-1"
            onClick={handleActionClick}
          >
            <button
              onClick={() => setEditTarget(row)}
              className="p-1.5 rounded-btn text-sc-text-muted hover:text-sc-primary hover:bg-sc-primary-light transition-colors"
              title="Edit attributes"
              aria-label={`Edit ${row.displayName}`}
            >
              <Settings size={15} />
            </button>
            <button
              onClick={() => setRevokeTarget(row)}
              className="p-1.5 rounded-btn text-sc-text-muted hover:text-sc-danger hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              title="Revoke access"
              aria-label={`Revoke ${row.displayName}`}
            >
              <ShieldX size={15} />
            </button>
          </div>
        ),
      },
    ],
    [handleActionClick],
  );

  if (techniciansQuery.isError) {
    return (
      <GlassCard className="text-center py-16">
        <Users size={48} className="mx-auto mb-4 text-sc-text-muted" />
        <h2 className="text-h3 font-display text-sc-navy mb-2">
          Unable to load technician data
        </h2>
        <p className="text-body-sm text-sc-text-muted">
          Microsoft Graph API may be experiencing issues.
        </p>
      </GlassCard>
    );
  }

  return (
    <div className="space-y-4">
      {/* Controls */}
      <GlassCard className="flex flex-wrap items-center gap-3 !p-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-sc-text-muted"
          />
          <input
            placeholder="Search technicians..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              debounceRef(e.target.value);
            }}
            className="h-9 w-full rounded-input border border-sc-border bg-sc-input-bg pl-9 pr-3 text-body-sm text-sc-text placeholder:text-sc-text-muted"
          />
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={() => setAddDialogOpen(true)}
        >
          <UserPlus size={16} />
          Add Technician
        </Button>
      </GlassCard>

      {/* Table */}
      <DataTable<TechnicianSummary>
        columns={columns}
        data={filtered}
        total={debouncedSearch ? filtered.length : total}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        sort={sort}
        onSortChange={setSort}
        isLoading={techniciansQuery.isLoading}
        rowKey={(row) => row.userId}
        onRowClick={(row) => {
          void navigate({
            to: "/technicians/$technicianId",
            params: { technicianId: row.userId },
          });
        }}
        emptyState={
          <EmptyState
            icon={<Users size={48} />}
            heading="No technicians found"
            description="Add technicians from Entra ID using the button above."
          />
        }
      />

      {/* Dialogs */}
      <AddTechnicianDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
      />
      <EditAttributesDialog
        open={!!editTarget}
        technician={editTarget}
        onClose={() => setEditTarget(null)}
      />
      <RevokeAccessDialog
        open={!!revokeTarget}
        technician={revokeTarget}
        onClose={() => setRevokeTarget(null)}
      />
    </div>
  );
}
